import moment from 'moment';

const MONTHS_DICT = {
    enero: 0, febrero: 1, marzo: 2, abril: 3, mayo: 4, junio: 5,
    julio: 6, agosto: 7, septiembre: 8, octubre: 9, noviembre: 10, diciembre: 11
};

// Placeholders conocidos que NO son fechas (los mismos que ignoramos en la migración).
const PLACEHOLDERS = new Set(['fecha invalida', 'no record', 'no date', 'nodate']);

function esPlaceholder(str) {
    if (!str || typeof str !== 'string') return false;
    const norm = str.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return PLACEHOLDERS.has(norm);
}

// Parsea el string español viejo "16 DE ABRIL 2024" (o "16 DE ABRIL DE 2024") a moment UTC.
// Fallback para documentos sin campo ISO migrado. Devuelve null si no se puede parsear.
function parseSpanishDate(str) {
    if (!str || typeof str !== 'string') return null;
    if (esPlaceholder(str)) return null;
    // separa por espacios y saca TODOS los "de" (cubre el "de" extra antes del año)
    const parts = str.trim().toLowerCase().split(/\s+/).filter(p => p !== 'de');
    if (parts.length < 3) return null;
    const month = MONTHS_DICT[parts[1]];
    if (month === undefined) return null;
    // moment.utc({...}) para NO proyectar a hora local: evita el corrimiento de día.
    const m = moment.utc({ day: +parts[0], month, year: +parts[2] });
    return m.isValid() ? m : null;
}

/**
 * Lectura genérica de una fecha de comercialInformation con doble lectura.
 * @param {object} course - la unidad académica
 * @param {string} isoField - nombre del campo ISO migrado (ej. 'initialDateISO')
 * @param {string} legacyField - nombre del campo string viejo (ej. 'initialDate')
 * @returns {moment.Moment|null} moment en UTC, o null si no hay fecha válida
 */
function readCourseDate(course, isoField, legacyField) {
    const ci = course?.comercialInformation;
    if (!ci) return null;
    // 1) Camino nuevo: campo ISO ya migrado
    if (ci[isoField]) return moment.utc(ci[isoField]);
    // 2) Fallback: string español viejo
    return parseSpanishDate(ci[legacyField]);
}

// --- Getters específicos: usá estos en los componentes ---

export function getCourseStartDate(course) {
    return readCourseDate(course, 'initialDateISO', 'initialDate');
}

export function getCourseEndDate(course) {
    return readCourseDate(course, 'endDateISO', 'endDate');
}

export function getCourseInitBreak(course) {
    return readCourseDate(course, 'initBreakISO', 'initBreak');
}

export function getCourseEndBreak(course) {
    return readCourseDate(course, 'endBreakISO', 'endBreak');
}

export function formatCourseStartDate(course) {
    const m = getCourseStartDate(course);
    if (!m) return "A confirmar";
    return m.locale("es").format("DD [DE] MMMM YYYY").toUpperCase(); // "16 DE ABRIL 2024"
}

export function getDaysUntilStart(course) {
    const target = getCourseStartDate(course); // moment UTC (ISO o fallback)
    if (!target) return null;

    const today = moment.utc().startOf('day');
    const diffDays = target.clone().startOf('day').diff(today, 'days');

    if (diffDays === 0) return "Empieza hoy";
    if (diffDays === 1) return "Empieza mañana";
    if (diffDays < 0) return "Ya comenzó. ¡Sumate!";
    return `Empieza en ${diffDays} días`;
}

// closeCampusDate vive en la raíz del documento, no en comercialInformation.
export function getCloseCampusDate(course) {
    if (!course) return null;
    if (course.closeCampusDateISO) return moment.utc(course.closeCampusDateISO);
    return parseSpanishDate(course.closeCampusDate);
}